import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Plus, Trash2 } from 'lucide-react';
import { api, pickLocale, formatApiErrorDetail } from '@/lib/api';

const empty = () => ({
  title: { en: '', hi: '', mr: '', gu: '' }, description: { en: '', hi: '', mr: '', gu: '' },
  file_url: '', kind: 'pdf', size: '',
});

export default function AdminMedia() {
  const qc = useQueryClient();
  const { data: items = [] } = useQuery({ queryKey: ['admin-media'], queryFn: async () => (await api.get('/media')).data });
  const [form, setForm] = useState(empty());

  const createMut = useMutation({
    mutationFn: async (d) => (await api.post('/admin/media', d)).data,
    onSuccess: () => { toast.success('Added'); qc.invalidateQueries(['admin-media']); qc.invalidateQueries(['media']); setForm(empty()); },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });
  const deleteMut = useMutation({
    mutationFn: async (id) => (await api.delete(`/admin/media/${id}`)).data,
    onSuccess: () => { qc.invalidateQueries(['admin-media']); qc.invalidateQueries(['media']); },
  });

  const setLoc = (f, lg) => (e) => setForm(x => ({ ...x, [f]: { ...x[f], [lg]: e.target.value } }));

  return (
    <div data-testid="admin-media-page">
      <h1 className="font-serif text-3xl text-ukgm-blue font-semibold">Manage Media Center</h1>

      <div className="mt-8 bg-white rounded-2xl border border-ukgm-gold/25 p-6 space-y-4">
        <div className="font-serif text-lg text-ukgm-blue font-semibold">Add new asset</div>
        <div className="grid md:grid-cols-2 gap-4">
          <input placeholder="File URL" value={form.file_url} onChange={e => setForm(f => ({ ...f, file_url: e.target.value }))} className="md:col-span-2 px-4 py-2.5 rounded-full bg-ukgm-cream text-sm" />
          <select value={form.kind} onChange={e => setForm(f => ({ ...f, kind: e.target.value }))} className="px-4 py-2.5 rounded-full bg-ukgm-cream text-sm">
            {['pdf', 'zip', 'image', 'video', 'other'].map(k => <option key={k}>{k}</option>)}
          </select>
          <input placeholder="Size (e.g. 2.4 MB)" value={form.size} onChange={e => setForm(f => ({ ...f, size: e.target.value }))} className="px-4 py-2.5 rounded-full bg-ukgm-cream text-sm" />
        </div>
        {['title', 'description'].map(field => (
          <div key={field}>
            <div className="text-xs tracking-widest uppercase text-ukgm-slate font-bold">{field}</div>
            <div className="mt-2 grid md:grid-cols-2 gap-3">
              {['en', 'hi', 'mr', 'gu'].map(lg => (
                <input key={lg} placeholder={lg.toUpperCase()} value={form[field][lg]} onChange={setLoc(field, lg)} className="px-4 py-2.5 rounded-full bg-ukgm-cream text-sm" />
              ))}
            </div>
          </div>
        ))}
        <button onClick={() => createMut.mutate(form)} className="inline-flex items-center gap-2 bg-ukgm-gold text-ukgm-blue font-bold px-5 py-2.5 rounded-full"><Plus className="w-4 h-4" /> Add</button>
      </div>

      <div className="mt-8 bg-white rounded-2xl border border-ukgm-gold/25 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-ukgm-cream">
            <tr className="text-left text-xs uppercase tracking-widest text-ukgm-slate">
              <th className="px-5 py-3">Title</th>
              <th className="px-5 py-3">Type</th>
              <th className="px-5 py-3">Size</th>
              <th className="px-5 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {items.map(m => (
              <tr key={m.id} className="border-t border-ukgm-gold/15">
                <td className="px-5 py-3 text-ukgm-charcoal font-medium">{pickLocale(m.title, 'en')}</td>
                <td className="px-5 py-3 text-ukgm-slate">{m.kind}</td>
                <td className="px-5 py-3 text-ukgm-slate">{m.size}</td>
                <td className="px-5 py-3 text-right">
                  <button onClick={() => window.confirm('Delete?') && deleteMut.mutate(m.id)} className="text-red-600 hover:text-red-700 text-xs font-bold inline-flex items-center gap-1"><Trash2 className="w-3 h-3" /> DEL</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
